// DefaultLocationModal.jsx
// A self-contained modal button component for setting default location coordinates
// from a map URL, showing only visibility-allowed employees.
// Usage: <DefaultLocationModal theme={theme} language={language} isRTL={isRTL} />

import React, { useState, useEffect, useCallback } from "react";
import { MapPin, X, Search, CheckCircle, AlertCircle, Loader, Link } from "lucide-react";
import { apiFetch } from "../context/AuthContext";
import { useProfile } from "../hooks/useProfile";

const BASE_URL = process.env.REACT_APP_BASE_URL || "https://nexploy-eg.com";

// ─── translations ────────────────────────────────────────────────────────────
const t = {
  en: {
    btnLabel: "Default Location",
    modalTitle: "Set Default Location",
    modalSubtitle: "Set a default map coordinate for an employee using a Google Maps link.",
    searchPlaceholder: "Search by name or ID…",
    noEmployees: "No employees found.",
    urlLabel: "Google Maps URL",
    urlPlaceholder: "Paste Google Maps link here…",
    save: "Save Location",
    saving: "Saving…",
    cancel: "Cancel",
    successMsg: "Default coordinates saved successfully!",
    errorMsg: "Failed to save coordinates.",
    urlRequired: "Please paste a map URL first.",
    empRequired: "Please select an employee first.",
    selected: "Selected",
    coords: "Coordinates",
  },
  ar: {
    btnLabel: "الموقع الافتراضي",
    modalTitle: "تعيين الموقع الافتراضي",
    modalSubtitle: "قم بتعيين إحداثيات خريطة افتراضية للموظف باستخدام رابط Google Maps.",
    searchPlaceholder: "ابحث بالاسم أو الرقم…",
    noEmployees: "لا يوجد موظفون.",
    urlLabel: "رابط Google Maps",
    urlPlaceholder: "الصق رابط Google Maps هنا…",
    save: "حفظ الموقع",
    saving: "جارٍ الحفظ…",
    cancel: "إلغاء",
    successMsg: "تم حفظ الإحداثيات بنجاح!",
    errorMsg: "فشل حفظ الإحداثيات.",
    urlRequired: "الرجاء لصق رابط خريطة أولاً.",
    empRequired: "الرجاء اختيار موظف أولاً.",
    selected: "المحدد",
    coords: "الإحداثيات",
  },
};

// ─── helpers ─────────────────────────────────────────────────────────────────
const resolveManagerContext = () => {
  try {
    const token = localStorage.getItem("token");
    let payload = null;
    if (token) {
      const parts = token.split(".");
      if (parts.length === 3) {
        let b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
        while (b64.length % 4) b64 += "=";
        payload = JSON.parse(atob(b64));
      }
    }
    return {
      clientName:
        localStorage.getItem("clientName") ||
        payload?.clientName ||
        payload?.employeeClientName ||
        "",
      managerCode:
        localStorage.getItem("infinityCode") ||
        payload?.infinityCode ||
        payload?.employeeInfinityCode ||
        null,
    };
  } catch {
    return { clientName: "", managerCode: null };
  }
};

// ─── main component ───────────────────────────────────────────────────────────
const DefaultLocationModal = ({ theme = "light", language = "en", isRTL = false }) => {
  const tr = t[language] || t.en;
  const isDark = theme === "dark";
  const { profile } = useProfile();

  // modal visibility
  const [open, setOpen] = useState(false);

  // employee list
  const [employees, setEmployees] = useState([]);
  const [loadingEmps, setLoadingEmps] = useState(false);
  const [search, setSearch] = useState("");

  // selection
  const [selectedEmp, setSelectedEmp] = useState(null);

  // map url
  const [mapUrl, setMapUrl] = useState("");

  // save state
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null); // { type: 'success'|'error', msg: string }

  // ── load employees when modal opens ────────────────────────────────────────
  const loadEmployees = useCallback(async () => {
    setLoadingEmps(true);
    setEmployees([]);
    try {
      const { clientName: fallbackClient, managerCode: fallbackCode } = resolveManagerContext();
      const clientName = profile?.clientName || fallbackClient;
      const managerCode = profile?.infinityCode || fallbackCode;
      const viewType = localStorage.getItem("managerViewType") || "manager";

      const usp = new URLSearchParams();
      if (clientName) usp.append("clientName", clientName);
      usp.append("viewType", viewType);
      if (viewType === "manager" && managerCode)
        usp.append("managerInfinityCode", managerCode);

      const resp = await apiFetch(
        `${BASE_URL}/employees/visibility?${usp.toString()}`,
        { method: "GET", headers: { "Accept-Language": language } }
      );
      if (!resp.ok) throw new Error("fetch failed");
      const json = await resp.json();
      const list = json.employees || json.data || [];
      setEmployees(
        list.map((e) => ({
          infinityCode: e.infinityCode || e.id,
          name:
            language === "ar"
              ? e.arabicName || e.sr || e.name || String(e.infinityCode)
              : e.sr || e.name || e.arabicName || String(e.infinityCode),
          clientName: e.clientName,
        }))
      );
    } catch {
      setEmployees([]);
    } finally {
      setLoadingEmps(false);
    }
  }, [language]);

  useEffect(() => {
    if (open) {
      loadEmployees();
      setSelectedEmp(null);
      setMapUrl("");
      setFeedback(null);
      setSearch("");
    }
  }, [open, loadEmployees]);

  // ── filtered list ──────────────────────────────────────────────────────────
  const filtered = employees.filter((e) => {
    const q = search.toLowerCase();
    return (
      e.name.toLowerCase().includes(q) ||
      String(e.infinityCode).toLowerCase().includes(q)
    );
  });

  // ── save handler ───────────────────────────────────────────────────────────
  const handleSave = async () => {
    setFeedback(null);
    if (!selectedEmp) {
      setFeedback({ type: "error", msg: tr.empRequired });
      return;
    }
    if (!mapUrl.trim()) {
      setFeedback({ type: "error", msg: tr.urlRequired });
      return;
    }

    setSaving(true);
    try {
      const resp = await apiFetch(
        `${BASE_URL}/employees/location/${selectedEmp.infinityCode}/default-coordinates`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Accept-Language": language,
          },
          body: JSON.stringify({ mapUrl: mapUrl.trim() }),
        }
      );
      const data = await resp.json();
      if (!resp.ok || !data.success) throw new Error(data.error || "error");

      setFeedback({
        type: "success",
        msg: `${tr.successMsg} (${data.data.defaultLatitude?.toFixed(5)}, ${data.data.defaultLongitude?.toFixed(5)})`,
      });
      // auto-close after 2.5 s
      setTimeout(() => setOpen(false), 2500);
    } catch (err) {
      setFeedback({ type: "error", msg: err.message || tr.errorMsg });
    } finally {
      setSaving(false);
    }
  };

  // ── style tokens ───────────────────────────────────────────────────────────
  const surface = isDark ? "#1e293b" : "#ffffff";
  const surfaceAlt = isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.02)";
  const border = isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.09)";
  const textPrimary = isDark ? "#f1f5f9" : "#0f172a";
  const textSecondary = isDark ? "#94a3b8" : "#64748b";
  const inputBg = isDark ? "rgba(71,85,105,0.4)" : "rgba(226,232,240,0.6)";
  const inputBorder = isDark
    ? "1px solid rgba(148,163,184,0.3)"
    : "1px solid rgba(148,163,184,0.3)";
  const accent = "#2e7cba";
  const accentHover = "#2563a8";

  const btnBase = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 16px",
    borderRadius: 8,
    border: "1px solid rgba(255,255,255,0.15)",
    fontWeight: 500,
    fontSize: 13,
    cursor: "pointer",
    transition: "all 0.2s ease",
    flexDirection: isRTL ? "row-reverse" : "row",
  };

  return (
    <>
      {/* ── trigger button (matches sibling action buttons in Attendance.jsx) ── */}
      <button
        onClick={() => setOpen(true)}
        style={{
          ...btnBase,
          background: isDark ? "rgba(30,41,59,0.8)" : "rgba(51,65,85,0.12)",
          color: isDark ? "#f1f5f9" : "#1e293b",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = isDark
            ? "rgba(30,41,59,1)"
            : "rgba(51,65,85,0.2)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = isDark
            ? "rgba(30,41,59,0.8)"
            : "rgba(51,65,85,0.12)";
        }}
      >
        <MapPin size={16} />
        {tr.btnLabel}
      </button>

      {/* ── modal ─────────────────────────────────────────────────────────────── */}
      {open && (
        <div
          onClick={() => !saving && setOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 99999,
            direction: isRTL ? "rtl" : "ltr",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: surface,
              borderRadius: 20,
              width: "min(96vw, 860px)",
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
              boxShadow: isDark
                ? "0 24px 64px rgba(0,0,0,0.7)"
                : "0 24px 64px rgba(0,0,0,0.18)",
              overflow: "hidden",
            }}
          >
            {/* header */}
            <div
              style={{
                padding: "24px 28px",
                borderBottom: `1px solid ${border}`,
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 16,
                flexShrink: 0,
              }}
            >
              <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 12,
                    background: `linear-gradient(135deg, ${accent}, #1d4ed8)`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <MapPin size={22} color="#fff" />
                </div>
                <div>
                  <h2
                    style={{
                      margin: 0,
                      fontSize: 20,
                      fontWeight: 700,
                      color: textPrimary,
                    }}
                  >
                    {tr.modalTitle}
                  </h2>
                  <p
                    style={{
                      margin: "4px 0 0",
                      fontSize: 13,
                      color: textSecondary,
                    }}
                  >
                    {tr.modalSubtitle}
                  </p>
                </div>
              </div>
              <button
                onClick={() => !saving && setOpen(false)}
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: saving ? "not-allowed" : "pointer",
                  color: textSecondary,
                  padding: 4,
                  borderRadius: 8,
                  display: "flex",
                  alignItems: "center",
                  flexShrink: 0,
                }}
              >
                <X size={22} />
              </button>
            </div>

            {/* body: two-column layout */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                flex: 1,
                overflow: "hidden",
              }}
            >
              {/* LEFT — employee list */}
              <div
                style={{
                  borderRight: isRTL ? "none" : `1px solid ${border}`,
                  borderLeft: isRTL ? `1px solid ${border}` : "none",
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                {/* search */}
                <div
                  style={{ padding: "16px 20px 10px", flexShrink: 0 }}
                >
                  <div style={{ position: "relative" }}>
                    <Search
                      size={15}
                      style={{
                        position: "absolute",
                        top: "50%",
                        [isRTL ? "right" : "left"]: 12,
                        transform: "translateY(-50%)",
                        color: textSecondary,
                        pointerEvents: "none",
                      }}
                    />
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder={tr.searchPlaceholder}
                      style={{
                        width: "100%",
                        padding: isRTL
                          ? "10px 36px 10px 12px"
                          : "10px 12px 10px 36px",
                        borderRadius: 10,
                        border: inputBorder,
                        background: inputBg,
                        color: textPrimary,
                        fontSize: 13,
                        outline: "none",
                        direction: isRTL ? "rtl" : "ltr",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>
                </div>

                {/* list */}
                <div
                  style={{
                    flex: 1,
                    overflowY: "auto",
                    padding: "4px 12px 16px",
                  }}
                >
                  {loadingEmps ? (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        height: 160,
                        gap: 12,
                        color: textSecondary,
                      }}
                    >
                      <Loader
                        size={28}
                        style={{ animation: "spin 1s linear infinite" }}
                      />
                      <span style={{ fontSize: 13 }}>Loading…</span>
                    </div>
                  ) : filtered.length === 0 ? (
                    <div
                      style={{
                        padding: "40px 16px",
                        textAlign: "center",
                        color: textSecondary,
                        fontSize: 13,
                      }}
                    >
                      {tr.noEmployees}
                    </div>
                  ) : (
                    filtered.map((emp) => {
                      const isActive =
                        selectedEmp?.infinityCode === emp.infinityCode;
                      return (
                        <div
                          key={emp.infinityCode}
                          onClick={() => {
                            setSelectedEmp(emp);
                            setFeedback(null);
                          }}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 12,
                            padding: "10px 12px",
                            borderRadius: 10,
                            cursor: "pointer",
                            marginBottom: 4,
                            background: isActive
                              ? isDark
                                ? "rgba(46,124,186,0.25)"
                                : "rgba(46,124,186,0.10)"
                              : "transparent",
                            border: isActive
                              ? `1px solid ${accent}55`
                              : "1px solid transparent",
                            transition: "all 0.15s ease",
                            direction: isRTL ? "rtl" : "ltr",
                          }}
                          onMouseEnter={(e) => {
                            if (!isActive)
                              e.currentTarget.style.background = surfaceAlt;
                          }}
                          onMouseLeave={(e) => {
                            if (!isActive)
                              e.currentTarget.style.background = "transparent";
                          }}
                        >
                          {/* avatar */}
                          <div
                            style={{
                              width: 36,
                              height: 36,
                              borderRadius: "50%",
                              background: isActive
                                ? `linear-gradient(135deg, ${accent}, #1d4ed8)`
                                : isDark
                                ? "rgba(255,255,255,0.10)"
                                : "rgba(0,0,0,0.07)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 13,
                              fontWeight: 700,
                              color: isActive
                                ? "#fff"
                                : textSecondary,
                              flexShrink: 0,
                            }}
                          >
                            {emp.name.charAt(0).toUpperCase()}
                          </div>

                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                fontSize: 13,
                                fontWeight: 600,
                                color: isActive ? accent : textPrimary,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {emp.name}
                            </div>
                            <div
                              style={{
                                fontSize: 11,
                                color: textSecondary,
                                marginTop: 1,
                              }}
                            >
                              #{emp.infinityCode}
                            </div>
                          </div>

                          {isActive && (
                            <CheckCircle
                              size={16}
                              color={accent}
                              style={{ flexShrink: 0 }}
                            />
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* RIGHT — URL input + save */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  padding: "24px 28px",
                  gap: 20,
                  overflowY: "auto",
                }}
              >
                {/* selected employee badge */}
                <div
                  style={{
                    padding: "12px 16px",
                    borderRadius: 12,
                    background: selectedEmp
                      ? isDark
                        ? "rgba(46,124,186,0.15)"
                        : "rgba(46,124,186,0.07)"
                      : surfaceAlt,
                    border: `1px solid ${
                      selectedEmp ? `${accent}44` : border
                    }`,
                    minHeight: 58,
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    transition: "all 0.2s ease",
                  }}
                >
                  {selectedEmp ? (
                    <>
                      <div
                        style={{
                          width: 38,
                          height: 38,
                          borderRadius: "50%",
                          background: `linear-gradient(135deg, ${accent}, #1d4ed8)`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 15,
                          fontWeight: 700,
                          color: "#fff",
                          flexShrink: 0,
                        }}
                      >
                        {selectedEmp.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: textPrimary,
                          }}
                        >
                          {selectedEmp.name}
                        </div>
                        <div
                          style={{ fontSize: 12, color: textSecondary }}
                        >
                          #{selectedEmp.infinityCode}
                        </div>
                      </div>
                    </>
                  ) : (
                    <span
                      style={{
                        fontSize: 13,
                        color: textSecondary,
                        fontStyle: "italic",
                      }}
                    >
                      ← {tr.searchPlaceholder.replace("…", "")}
                    </span>
                  )}
                </div>

                {/* URL field */}
                <div>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      fontSize: 13,
                      fontWeight: 600,
                      color: textPrimary,
                      marginBottom: 8,
                    }}
                  >
                    <Link size={14} />
                    {tr.urlLabel}
                  </label>
                  <textarea
                    value={mapUrl}
                    onChange={(e) => {
                      setMapUrl(e.target.value);
                      setFeedback(null);
                    }}
                    placeholder={tr.urlPlaceholder}
                    rows={4}
                    style={{
                      width: "100%",
                      padding: "12px 14px",
                      borderRadius: 12,
                      border: inputBorder,
                      background: inputBg,
                      color: textPrimary,
                      fontSize: 13,
                      resize: "vertical",
                      outline: "none",
                      direction: "ltr",
                      fontFamily: "monospace",
                      boxSizing: "border-box",
                      transition: "border-color 0.2s",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = accent;
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "";
                    }}
                  />
                  <p
                    style={{
                      fontSize: 11,
                      color: textSecondary,
                      margin: "6px 0 0",
                    }}
                  >
                    Supported: Google Maps links, short links (goo.gl/maps),
                    share URLs with @lat,lng, etc.
                  </p>
                </div>

                {/* feedback */}
                {feedback && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 10,
                      padding: "12px 14px",
                      borderRadius: 10,
                      background:
                        feedback.type === "success"
                          ? isDark
                            ? "rgba(16,185,129,0.15)"
                            : "rgba(16,185,129,0.08)"
                          : isDark
                          ? "rgba(239,68,68,0.15)"
                          : "rgba(239,68,68,0.08)",
                      border: `1px solid ${
                        feedback.type === "success"
                          ? "rgba(16,185,129,0.4)"
                          : "rgba(239,68,68,0.4)"
                      }`,
                    }}
                  >
                    {feedback.type === "success" ? (
                      <CheckCircle
                        size={16}
                        color="#10b981"
                        style={{ flexShrink: 0, marginTop: 1 }}
                      />
                    ) : (
                      <AlertCircle
                        size={16}
                        color="#ef4444"
                        style={{ flexShrink: 0, marginTop: 1 }}
                      />
                    )}
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 500,
                        color:
                          feedback.type === "success" ? "#10b981" : "#ef4444",
                        lineHeight: 1.4,
                      }}
                    >
                      {feedback.msg}
                    </span>
                  </div>
                )}

                {/* spacer */}
                <div style={{ flex: 1 }} />

                {/* action buttons */}
                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    flexDirection: isRTL ? "row-reverse" : "row",
                    justifyContent: "flex-end",
                  }}
                >
                  <button
                    onClick={() => !saving && setOpen(false)}
                    disabled={saving}
                    style={{
                      padding: "10px 20px",
                      borderRadius: 10,
                      border: `1px solid ${border}`,
                      background: "transparent",
                      color: textSecondary,
                      fontWeight: 600,
                      fontSize: 13,
                      cursor: saving ? "not-allowed" : "pointer",
                      transition: "all 0.2s",
                    }}
                  >
                    {tr.cancel}
                  </button>

                  <button
                    onClick={handleSave}
                    disabled={saving}
                    style={{
                      padding: "10px 24px",
                      borderRadius: 10,
                      border: "none",
                      background: saving
                        ? isDark
                          ? "rgba(46,124,186,0.5)"
                          : "rgba(46,124,186,0.4)"
                        : `linear-gradient(135deg, ${accent}, #1d4ed8)`,
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: 13,
                      cursor: saving ? "not-allowed" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      transition: "all 0.2s",
                      boxShadow: saving
                        ? "none"
                        : `0 4px 14px ${accent}55`,
                    }}
                    onMouseEnter={(e) => {
                      if (!saving)
                        e.currentTarget.style.background = `linear-gradient(135deg, ${accentHover}, #1e40af)`;
                    }}
                    onMouseLeave={(e) => {
                      if (!saving)
                        e.currentTarget.style.background = `linear-gradient(135deg, ${accent}, #1d4ed8)`;
                    }}
                  >
                    {saving ? (
                      <Loader
                        size={15}
                        style={{ animation: "spin 1s linear infinite" }}
                      />
                    ) : (
                      <MapPin size={15} />
                    )}
                    {saving ? tr.saving : tr.save}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* spin keyframe */}
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    </>
  );
};

export default DefaultLocationModal;